import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spin } from "antd";
import PageHeader from "./components/PageHeader";
import TabNav from "./components/TabNav";
import GlossaryDrawer from "./components/GlossaryDrawer";
import ToastHost from "./components/ToastHost";
import UnsavedChangesDialog from "./components/UnsavedChangesDialog";
import ConfigurationsPanel from "./configurations/ConfigurationsPanel";
import CataloguePanel from "./catalogue/CataloguePanel";
import AssumptionsPanel from "./assumptions/AssumptionsPanel";
import MeasuredDataPanel from "./measured-data/MeasuredDataPanel";
import type { RootState } from "../../redux/appState";
import type { CoilTab } from "../../redux/coilConfigurator/ui/reducer";
import ConfigurationModal from "./configurations/ConfigurationModal";
import {
  clearToast,
  loadApplicationState,
  openConfigModal,
  revertAssumptionsDraft,
  revertCatalogueDraft,
  saveAssumptions,
  saveCatalogue,
  setActiveTab,
  setGlossaryOpen,
} from "../../redux/coilConfigurator/actions";
import {
  selectAssumptionsDirty,
  selectCatalogueDirty,
} from "../../redux/coilConfigurator/selectors";
import "./coil-configurator-legacy.css";
import "./coil-configurator.css";
import "./components/cc-button.css";
import "./components/cc-dialog.css";

const TAB_LABELS: Record<CoilTab, string> = {
  configurations: "Configurations",
  catalogue: "Catalogue",
  assumptions: "Assumptions",
  "measured-data": "Measured Data",
};

type UnsavedSourceTab = "catalogue" | "assumptions";

type PendingTabSwitch = {
  targetTab: CoilTab;
  sourceTab: UnsavedSourceTab;
};

const CoilConfiguratorPage = () => {
  const dispatch = useDispatch();
  const activeTab = useSelector((state: RootState) => state.coilConfigurator.ui.activeTab);
  const loadStatus = useSelector((state: RootState) => state.coilConfigurator.persistence.loadStatus);
  const toast = useSelector((state: RootState) => state.coilConfigurator.ui.toast);
  const glossaryOpen = useSelector((state: RootState) => state.coilConfigurator.ui.glossaryOpen);
  const catalogueDirty = useSelector(selectCatalogueDirty);
  const assumptionsDirty = useSelector(selectAssumptionsDirty);

  const [pendingSwitch, setPendingSwitch] = useState<PendingTabSwitch | null>(null);
  const [savePending, setSavePending] = useState(false);

  useEffect(() => {
    dispatch(loadApplicationState());
  }, [dispatch]);

  const completeSwitch = useCallback(
    (targetTab: CoilTab) => {
      dispatch(setActiveTab(targetTab));
      setPendingSwitch(null);
      setSavePending(false);
    },
    [dispatch],
  );

  const handleTabChange = useCallback(
    (nextTab: CoilTab) => {
      if (nextTab === activeTab) return;

      if (activeTab === "catalogue" && catalogueDirty) {
        setPendingSwitch({ targetTab: nextTab, sourceTab: "catalogue" });
        return;
      }

      if (activeTab === "assumptions" && assumptionsDirty) {
        setPendingSwitch({ targetTab: nextTab, sourceTab: "assumptions" });
        return;
      }

      dispatch(setActiveTab(nextTab));
    },
    [activeTab, assumptionsDirty, catalogueDirty, dispatch],
  );

  const closeUnsavedDialog = () => {
    setPendingSwitch(null);
    setSavePending(false);
  };

  const handleDiscard = () => {
    if (!pendingSwitch) return;

    if (pendingSwitch.sourceTab === "catalogue") {
      dispatch(revertCatalogueDraft());
    } else {
      dispatch(revertAssumptionsDraft());
    }

    completeSwitch(pendingSwitch.targetTab);
  };

  const handleSaveAndSwitch = () => {
    if (!pendingSwitch) return;

    setSavePending(true);

    if (pendingSwitch.sourceTab === "catalogue") {
      dispatch(saveCatalogue());
    } else {
      dispatch(saveAssumptions());
    }
  };

  useEffect(() => {
    if (!savePending || !pendingSwitch) return;

    const stillDirty =
      pendingSwitch.sourceTab === "catalogue" ? catalogueDirty : assumptionsDirty;

    if (!stillDirty) {
      completeSwitch(pendingSwitch.targetTab);
    }
  }, [
    assumptionsDirty,
    catalogueDirty,
    completeSwitch,
    pendingSwitch,
    savePending,
  ]);

  useEffect(() => {
    if (savePending && toast?.type === "error") {
      setSavePending(false);
    }
  }, [savePending, toast]);

  if (loadStatus === "loading" || loadStatus === "idle") {
    return (
      <div className="coil-configurator-loading">
        <Spin size="large" tip="Loading coil configurator..." />
      </div>
    );
  }

  return (
    <div className="app-shell coil-configurator-shell">
      <div className="coil-configurator-main">
        <PageHeader
          onOpenGlossary={() => dispatch(setGlossaryOpen(true))}
          onCreateConfiguration={() => dispatch(openConfigModal("create"))}
        />

        <div className="content-container">
          <TabNav activeTab={activeTab} onChange={handleTabChange} />

          {activeTab === "configurations" && <ConfigurationsPanel />}
          {activeTab === "catalogue" && <CataloguePanel />}
          {activeTab === "assumptions" && <AssumptionsPanel />}
          {activeTab === "measured-data" && <MeasuredDataPanel />}
        </div>
      </div>

      <GlossaryDrawer open={glossaryOpen} onClose={() => dispatch(setGlossaryOpen(false))} />
      <ConfigurationModal />
      <ToastHost
        message={toast?.message ?? null}
        type={toast?.type ?? "success"}
        onClear={() => dispatch(clearToast())}
      />
      <UnsavedChangesDialog
        open={pendingSwitch != null}
        tabLabel={pendingSwitch ? TAB_LABELS[pendingSwitch.sourceTab] : ""}
        saving={savePending}
        onCancel={closeUnsavedDialog}
        onDiscard={handleDiscard}
        onSave={handleSaveAndSwitch}
      />
    </div>
  );
};

export default CoilConfiguratorPage;
